import React, { createContext, useContext, useEffect, useState } from 'react';
import { UserPreferences } from '@/types';

type UIMode = NonNullable<UserPreferences['uiMode']>;

interface UIModeContextType {
  uiMode: UIMode;
  setUIMode: (mode: UIMode) => void;
}

const UIModeContext = createContext<UIModeContextType | undefined>(undefined);

export function UIModeProvider({ children }: { children: React.ReactNode }) {
  // Default to ambient as discussed
  const [uiMode, setUIModeState] = useState<UIMode>('ambient');

  useEffect(() => {
    // Load from localStorage on mount
    const stored = localStorage.getItem('khiimori:uiMode');
    let initialMode: UIMode = 'ambient';
    if (stored === 'invisible' || stored === 'ambient' || stored === 'explicit') {
      initialMode = stored as UIMode;
      setUIModeState(initialMode);
    }
    
    // Broadcast initial mode to extension
    window.postMessage({
      source: 'khiimori-app',
      action: 'setUIMode',
      payload: { mode: initialMode }
    }, '*');
  }, []);

  const setUIMode = (mode: UIMode) => {
    setUIModeState(mode);
    localStorage.setItem('khiimori:uiMode', mode);
    
    // Dispatch an event so other tabs/components not in react tree can listen if needed
    window.dispatchEvent(new CustomEvent('khiimori:uiModeChanged', { detail: mode }));
    
    // Broadcast to the browser extension bridge
    window.postMessage({
      source: 'khiimori-app',
      action: 'setUIMode',
      payload: { mode }
    }, '*');
  };

  return (
    <UIModeContext.Provider value={{ uiMode, setUIMode }}>
      {children}
    </UIModeContext.Provider>
  );
}

export function useUIMode() {
  const context = useContext(UIModeContext);
  if (context === undefined) {
    throw new Error('useUIMode must be used within a UIModeProvider');
  }
  return context;
}
